import React, { useEffect, useRef } from 'react';

// Mouse-reactive particle dust layer (Active Theory-inspired, vanilla canvas).
// Periwinkle dust drifts in the dark; the cursor acts like wind and pushes it around.
const ParticleField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    // Headless/automated browsers: skip the perpetual rAF loop (it breaks
    // virtual-time screenshot capture used for visual verification).
    if (navigator.webdriver) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let width = 0, height = 0;
    let particles = [];
    let raf = 0;
    let running = true;
    const mouse = { x: -9999, y: -9999, vx: 0, vy: 0, px: -9999, py: -9999 };

    const spawn = (w, h) => {
      const count = Math.min(Math.floor((w * h) / 1100), 2600);
      particles = Array.from({ length: count }, () => {
        const spark = Math.random() < 0.06; // a few bright glowing sparks
        return {
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.2,
          vy: (Math.random() - 0.5) * 0.2,
          r: spark ? 1.8 + Math.random() * 1.2 : 0.7 + Math.random() * 1.3,
          spark,
          // mostly indigo dust, some deep-navy sparks (light background)
          hue: Math.random() < 0.7 ? '99,102,241' : '34,28,81',
          a: spark ? 0.45 + Math.random() * 0.3 : 0.14 + Math.random() * 0.34,
          phase: Math.random() * Math.PI * 2,
        };
      });
    };

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      spawn(width, height);
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      mouse.vx = x - (mouse.px === -9999 ? x : mouse.px);
      mouse.vy = y - (mouse.py === -9999 ? y : mouse.py);
      mouse.px = x; mouse.py = y;
      mouse.x = x; mouse.y = y;
    };
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999; mouse.px = -9999; mouse.py = -9999; };

    let t = 0;
    const tick = () => {
      if (!running) return;
      t += 0.004;
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // slow ambient drift (cheap pseudo-noise)
        p.vx += Math.sin(t * 2 + p.phase + p.y * 0.002) * 0.002;
        p.vy += Math.cos(t * 1.6 + p.phase + p.x * 0.002) * 0.002;

        // mouse wind — cursor velocity pushes nearby dust
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < 16900) { // 130px radius
          const d = Math.sqrt(d2) || 1;
          const force = (1 - d / 130) * 0.5;
          p.vx += (dx / d) * force + mouse.vx * 0.012 * force;
          p.vy += (dy / d) * force + mouse.vy * 0.012 * force;
        }

        // damping + move
        p.vx *= 0.96;
        p.vy *= 0.96;
        p.x += p.vx;
        p.y += p.vy;

        // wrap edges
        if (p.x < -5) p.x = width + 5; else if (p.x > width + 5) p.x = -5;
        if (p.y < -5) p.y = height + 5; else if (p.y > height + 5) p.y = -5;

        // twinkle
        const alpha = p.a * (0.65 + 0.35 * Math.sin(t * 3 + p.phase));
        ctx.fillStyle = `rgba(${p.hue},${alpha})`;
        if (p.spark) {
          ctx.shadowColor = `rgba(${p.hue},0.9)`;
          ctx.shadowBlur = 8;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        } else {
          ctx.fillRect(p.x, p.y, p.r, p.r);
        }
      }
      raf = requestAnimationFrame(tick);
    };

    // Reduced motion: draw one static dust frame, no animation loop
    const drawStatic = () => {
      ctx.clearRect(0, 0, width, height);
      for (const p of particles) {
        ctx.fillStyle = `rgba(${p.hue},${p.a})`;
        ctx.fillRect(p.x, p.y, p.r, p.r);
      }
    };

    const onVisibility = () => {
      running = document.visibilityState === 'visible';
      if (running) { raf = requestAnimationFrame(tick); } else { cancelAnimationFrame(raf); }
    };

    const onResize = () => { resize(); if (reduced) drawStatic(); };

    resize();
    if (reduced) {
      drawStatic();
    } else {
      raf = requestAnimationFrame(tick);
      canvas.parentElement.addEventListener('mousemove', onMove);
      canvas.parentElement.addEventListener('mouseleave', onLeave);
      document.addEventListener('visibilitychange', onVisibility);
    }
    window.addEventListener('resize', onResize);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      canvas.parentElement.removeEventListener('mousemove', onMove);
      canvas.parentElement.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}
    />
  );
};

export default ParticleField;
